import { ContainerModule } from 'inversify';
import 'reflect-metadata';

import { TYPES as ApiGatewayTypes, type IApiGateway } from '@desygner/ui-core-api-gateway';
import {
  FluerCollaborateService,
  ICollaborateService,
  WebrandCollaborateService,
} from '@desygner/ui-data-access-collaborate';
import { TYPES as COLLABORATE_SERVICE_TYPES } from '@desygner/ui-data-access-collaborate';
import {
  IUserService,
  TYPES as USER_SERVICE_TYPE,
  UserService,
} from '@desygner/ui-data-access-user';
import {
  CompanyService,
  ICompanyService,
  IWorkflowsService,
  TYPES as WORKSPACE_SERVICE_TYPES,
  WorkflowsService,
} from '@desygner/ui-data-access-workspace';

import { TYPES } from '../inversify.config';
import { AnimationManager } from './viewer/animation-manager';
import { CredentialsManager, ICredentialsManager } from './viewer/credentials-manager';
import { ErrorManager, IErrorManager } from './viewer/error-manager';
import {
  IKeyboardShortcutsManager,
  KeyboardShortcutsManager,
} from './viewer/keyboard-shortcuts-manager';
import { PageManager } from './viewer/page-manager';
import { IProjectManager, ProjectManager } from './viewer/project-manager';
import { RestrictionsManager } from './viewer/restrictions-manager';
import { SVGManager } from './viewer/svg-manager';
import { VideoManager } from './viewer/video-manager';
import { VirtualizerService } from './viewer/virtualizer-service';
import { IWorkflowManager, WorkflowManager } from './viewer/workflow-manager';

const serviceContainer = new ContainerModule((bind) => {
  bind<AnimationManager>(TYPES.Viewer.AnimationManager).to(AnimationManager).inSingletonScope();

  bind<ICredentialsManager>(TYPES.Viewer.CredentialsManager)
    .to(CredentialsManager)
    .inSingletonScope();

  bind<ICollaborateService>(COLLABORATE_SERVICE_TYPES.CollaborateService)
    .to(process.env.BRAND === 'fluer' ? FluerCollaborateService : WebrandCollaborateService)
    .inSingletonScope();

  bind<IErrorManager>(TYPES.Viewer.ErrorManager).to(ErrorManager).inSingletonScope();

  bind<IKeyboardShortcutsManager>(TYPES.Viewer.KeyboardShortcutsManager)
    .to(KeyboardShortcutsManager)
    .inSingletonScope();

  bind<PageManager>(TYPES.Viewer.PageManager).to(PageManager).inSingletonScope();

  bind<IProjectManager>(TYPES.Viewer.ProjectManager).to(ProjectManager).inSingletonScope();

  bind<RestrictionsManager>(TYPES.Viewer.RestrictionsManager)
    .to(RestrictionsManager)
    .inSingletonScope();

  bind<IUserService>(USER_SERVICE_TYPE.UserService)
    .toDynamicValue((context) => {
      const instance = process.env.BRAND === 'fluer' ? 'fluer' : null;

      const apiGateway = context.container.get<IApiGateway>(ApiGatewayTypes.ApiGateway);

      return new UserService(apiGateway, instance);
    })
    .inSingletonScope();

  bind<VirtualizerService>(TYPES.Viewer.VirtualizerService)
    .to(VirtualizerService)
    .inSingletonScope();

  bind(TYPES.Viewer.SVGManager).to(SVGManager).inSingletonScope();

  bind(TYPES.Viewer.VideoManager).to(VideoManager).inSingletonScope();

  bind<ICompanyService>(WORKSPACE_SERVICE_TYPES.CompanyService)
    .to(CompanyService)
    .inSingletonScope();

  bind<IWorkflowsService>(WORKSPACE_SERVICE_TYPES.WorkflowsService)
    .to(WorkflowsService)
    .inSingletonScope();

  bind<IWorkflowManager>(TYPES.Viewer.WorkflowManager).to(WorkflowManager).inSingletonScope();
});

export { serviceContainer, TYPES };
